
import { Shield, FileCheck, BarChart3, AlertTriangle, Lock, Scale } from "lucide-react";
import { LandingPageTemplate } from "@/components/layout/LandingPageTemplate";
import { HeroSection } from "@/components/landing/HeroSection";
import { FeatureSection } from "@/components/landing/FeatureSection";
import { ProcessSection } from "@/components/landing/ProcessSection";
import { BenefitSection } from "@/components/landing/BenefitSection";
import { CtaSection } from "@/components/landing/CtaSection";

const Nist800171Assessment = () => {
  return (
    <LandingPageTemplate
      title="NIST 800-171 Assessment"
      description="Comprehensive NIST 800-171 assessment for organizations handling CUI and working with the Department of Defense."
      keywords={[
        "NIST 800-171",
        "CUI protection",
        "DoD compliance",
        "defense contractors",
        "security assessment"
      ]}
      canonicalUrl="/cybersecurity/nist-800-171-assessment/"
      schemaType="service"
      schemaData={{
        name: "NIST 800-171 Assessment Services",
        description: "Expert evaluation of your CUI protection controls",
        provider: {
          name: "edgefi",
          sameAs: "https://edgefi.com"
        }
      }}
    >
      <HeroSection
        title="Defense Contractor"
        highlighted="NIST 800-171 Assessment"
        description="Expert evaluation of your organization's controls for protecting Controlled Unclassified Information (CUI)."
        ctaText="Schedule Assessment"
        heroPattern="roadmap"
      />

      <FeatureSection 
        title="Comprehensive NIST 800-171 Assessment"
        subtitle="KEY FEATURES"
        description="Our assessment provides a thorough evaluation of your CUI protection controls."
        features={[
          {
            title: "CUI Controls",
            description: "Evaluate your controls for protecting Controlled Unclassified Information.",
            icon: <Shield className="h-6 w-6 text-purple-600" />
          },
          {
            title: "Gap Analysis",
            description: "Identify gaps between current controls and NIST 800-171 requirements.",
            icon: <AlertTriangle className="h-6 w-6 text-purple-600" />
          },
          {
            title: "Documentation",
            description: "Review of System Security Plan (SSP) and related documentation.",
            icon: <FileCheck className="h-6 w-6 text-purple-600" />
          },
          {
            title: "Risk Assessment",
            description: "Evaluation of risks to CUI and control effectiveness.",
            icon: <BarChart3 className="h-6 w-6 text-purple-600" />
          },
          {
            title: "Compliance Score",
            description: "Calculate your NIST 800-171 compliance score.",
            icon: <Scale className="h-6 w-6 text-purple-600" />
          },
          {
            title: "Access Controls",
            description: "Assessment of CUI access controls and protection.",
            icon: <Lock className="h-6 w-6 text-purple-600" />
          }
        ]}
      />

      <ProcessSection
        title="Our Assessment Approach"
        subtitle="METHODOLOGY"
        description="A systematic approach to evaluating your CUI protection controls"
        steps={[
          {
            title: "Initial Scoping",
            description: "Define assessment boundaries and identify CUI systems"
          },
          {
            title: "Control Review",
            description: "Assess implementation of NIST 800-171 controls"
          },
          {
            title: "Gap Analysis",
            description: "Identify compliance gaps and security weaknesses"
          },
          {
            title: "Score Calculation",
            description: "Calculate NIST 800-171 compliance score"
          },
          {
            title: "Recommendations",
            description: "Develop detailed recommendations and POA&M"
          }
        ]}
      />

      <BenefitSection
        title="Why Choose Our NIST 800-171 Assessment"
        benefits={[
          {
            title: "DoD Expertise",
            description: "Work with specialists in defense contracting requirements"
          },
          {
            title: "Comprehensive Coverage",
            description: "Full evaluation of all 110 security requirements"
          },
          {
            title: "Clear Action Plan",
            description: "Detailed POA&M for addressing gaps"
          },
          {
            title: "Regulatory Alignment",
            description: "Ensure compliance with DoD requirements"
          },
          {
            title: "Risk Reduction",
            description: "Identify and address CUI protection gaps"
          },
          {
            title: "Future Readiness",
            description: "Prepare for CMMC certification"
          }
        ]}
      />

      <CtaSection 
        title="Ready to Assess Your NIST 800-171 Compliance?"
        description="Schedule your NIST 800-171 assessment today and ensure proper CUI protection."
        ctaText="Schedule a Consultation"
        background="purple"
        modalTitle="Request NIST 800-171 Assessment"
        modalDescription="Tell us about your organization's needs for NIST 800-171 compliance."
        additionalFields={{
          lead_source: "Website",
          lead_interest: "NIST 800-171 Assessment",
          service_interest: "Security Assessment"
        }}
      />
    </LandingPageTemplate>
  );
};

export default Nist800171Assessment;
